import { useEffect, useState } from 'react'

import { usePrefersReducedMotion } from '@/lib/motion'

export interface HomeMetric {
  value: number
  label: string
}

const COUNT_DURATION_MS = 1400

function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3)
}

function useCountUp(target: number, enabled: boolean) {
  const [display, setDisplay] = useState(0)

  useEffect(() => {
    if (!enabled) return

    let raf = 0
    const start = performance.now()

    const tick = (now: number) => {
      const elapsed = now - start
      const progress = Math.min(Math.max(elapsed / COUNT_DURATION_MS, 0), 1)
      setDisplay(Math.round(target * easeOutCubic(progress)))
      if (progress < 1) raf = requestAnimationFrame(tick)
    }

    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, enabled])

  return enabled ? display : target
}

function AnimatedMetricValue({ value }: { value: number }) {
  const reduceMotion = usePrefersReducedMotion()
  const display = useCountUp(value, !reduceMotion)
  const formatted = new Intl.NumberFormat('en-US').format(display)

  return <>{formatted}</>
}

export const HomeMetrics = ({ metrics }: { metrics: HomeMetric[] }) => {
  return (
    <div className="mx-auto flex w-full max-w-[520px] items-center justify-center sm:max-w-[560px]">
      {metrics.map((metric, index) => (
        <>
          <div
            key={metric.label}
            className="min-w-0 flex-1 px-0.5 py-2.5 text-center sm:px-1.5 sm:py-3"
          >
            <div className="text-base font-semibold tracking-tight text-white tabular-nums sm:text-2xl">
              <AnimatedMetricValue value={metric.value} />
            </div>
            <div className="mt-0.5 text-xs leading-tight font-medium text-[var(--foreground-muted)] sm:mt-1 sm:text-xs dark:text-slate-300">
              {metric.label}
            </div>
          </div>
          {index < metrics.length - 1 && (
            <div
              key={`divider-${index}`}
              aria-hidden
              className="h-[92px] w-px shrink-0"
              style={{
                background:
                  'linear-gradient(180deg, var(--background) 0%, #E0A263 50%, var(--background) 100%)',
              }}
            />
          )}
        </>
      ))}
    </div>
  )
}
